import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { useFieldArray } from "react-hook-form";
import { UseFormReturn } from "react-hook-form/dist/types/form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FormInput } from "go-form";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { ApplicationUpdateApi } from "../../../../../../../../../services/Api/Organization/types";
import { SearchSelectApi } from "../../../../../../../../../services/Api/types";
import { AppState } from "../../../utils/types";

interface Props {
	appState: AppState;
	form: UseFormReturn<ApplicationUpdateApi>;
}

const HolidayPmsForm = ({ appState, form }: Props) => {
	const [updatedConfig, setUpdatedConfig] = useState<Record<string, any> | undefined>(appState.config);
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const {
		register,
		formState: { errors },
		control,
		watch,
	} = form;
	const { fields, append, remove } = useFieldArray({ name: "settings.terminals", control });

	const searchPaymentMethod = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getPaymentMethodsSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const updateConfig = (field: string, obj?: SearchSelectApi) => {
		const cnf = { ...updatedConfig };
		if (field === "payment_method_name") {
			cnf.payment_method_name = obj?.label;
		}
		setUpdatedConfig(cnf);
	};

	const searchTerminals = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const alreadyUsedTerminals = watch("settings.terminals");
		const alreadyUsedTerminalUids = alreadyUsedTerminals?.reduce(
			(terminalUids: string, terminal: Record<string, any>) => {
				if (terminalUids === "") return terminal.TERMINAL_UID;
				return `${terminalUids}${FILTER_VALUE_SEPARATOR}${terminal.TERMINAL_UID}`;
			},
			""
		);

		return apiOrganization.getTerminalsSearchSelect(
			search,
			params,
			{ cancelToken: options?.token },
			alreadyUsedTerminalUids
		);
	};

	return (
		<>
			<FormSelectGroup
				name="settings.payment_method_id"
				getOptionValue={(option) => option.id}
				path={`/${organization.id}/settings/payment_methods/`}
				control={control}
				onChange={(_, fullObj) => updateConfig("payment_method_name", fullObj)}
				loadOptions={searchPaymentMethod}
				label={t("modules.app.field.default_payment_method.title")}
				defaultValue={{
					label: updatedConfig?.payment_method_name,
					id: appState.app?.settings?.payment_method_id,
				}}
				errors={errors}
				data-testid="settings.payment_method_id"
			/>
			<FormInput
				name="settings.api_key"
				errors={errors}
				register={register}
				label={t("modules.app.field.api_key.title")}
			/>
			<FormInput
				name="settings.resort_id"
				errors={errors}
				register={register}
				label={t("modules.app.field.resort_id.title")}
			/>
			<FormInput
				name="settings.url"
				errors={errors}
				register={register}
				label={t("modules.app.field.url.title")}
			/>
			<table className="table table-form">
				<thead>
					<tr>
						<th className="action" />
						<th>{t("common.word.terminal")}</th>
						<th>{t("modules.app.field.key.title")}</th>
						<th className="action" />
					</tr>
				</thead>
				<tbody>
					{fields.map((field: Record<string, any>, index) => {
						return (
							<tr key={field.id}>
								<td className="action" />
								<td className="w-50">
									<FormSelectGroup
										name={`settings.terminals.${index}.TERMINAL_UID`}
										errors={errors}
										control={control}
										loadOptions={searchTerminals}
										getOptionValue={(option) => option.uid}
										defaultValue={{
											uid: field.TERMINAL_UID,
											label: field.TERMINAL_NAME,
										}}
									/>
								</td>
								<td>
									<FormInput
										name={`settings.terminals.${index}.TERMINAL_KEY`}
										register={register}
										errors={errors}
									/>
								</td>
								<td className="action">
									<RemoveSVG onClick={() => remove(index)} className="icon" />
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<Button variant="add" className="d-block" onClick={() => append({ TERMINAL_UID: "", TERMINAL_KEY: "" })}>
				+ {t("common.action.add", { ns: "lib" })}
			</Button>
		</>
	);
};

export default HolidayPmsForm;
